'use client';

import {
  Button,
  Modal,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalIcon,
  ModalTitle,
} from '@lulalife/design-system';

type SettingsReassignUsersModalProps = {
  open: boolean;
  roleName: string;
  usersCount: number;
  /** Navigate to the users assigned to this role, so they can be moved off it. */
  onViewUsers: () => void;
  onClose: () => void;
  'data-testid'?: string;
};

/**
 * The block a role with users still on it hits when someone tries to delete
 * it — PEG-861/PEG-862. Deleting out from under assigned users would orphan
 * them, so this replaces the type-to-confirm delete modal for that case and
 * points at the fix (move them to another role first) instead.
 *
 * Composed from the same DS `Modal` primitives as `SettingsDiscardModal`; no
 * DS-GAP marker needed. `data-lula-ds` goes on `Modal` itself for the same
 * portal-escapes-every-marked-ancestor reason documented there.
 */
export const SettingsReassignUsersModal = ({
  open,
  roleName,
  usersCount,
  onViewUsers,
  onClose,
  'data-testid': dataTestId,
}: SettingsReassignUsersModalProps) => (
  <Modal
    open={open}
    onOpenChange={(nextOpen) => {
      if (!nextOpen) onClose();
    }}
    size="small"
    showCloseButton={false}
    data-lula-ds=""
    data-testid={dataTestId}
  >
    <ModalHeader icon={<ModalIcon type="danger" />}>
      <ModalTitle>Reassign users before deleting</ModalTitle>
      <ModalDescription className="text-text-primary">
        {roleName} is assigned to {usersCount} {usersCount === 1 ? 'user' : 'users'}. Move them to
        another role first, then delete this one.
      </ModalDescription>
    </ModalHeader>
    <ModalFooter>
      <Button variant="secondary" type="button" onClick={onClose}>
        Close
      </Button>
      <Button variant="primary" type="button" onClick={onViewUsers}>
        View users
      </Button>
    </ModalFooter>
  </Modal>
);

export default SettingsReassignUsersModal;
